import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Network, Eye, EyeOff, AlertCircle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CACHE_KEY_LOGO = 'login_logo_url';
const CACHE_KEY_BG = 'login_bg_url';
const DEFAULT_BG = 'https://images.unsplash.com/photo-1683770997177-0603bd44d070?crop=entropy&cs=srgb&fm=jpg&q=85';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => {
    try {
      const r = new URLSearchParams(window.location.search).get('reason');
      return r === 'expired' ? 'Tu sesión ha expirado. Por favor inicia sesión nuevamente.' : '';
    } catch { return ''; }
  });
  const [logoImage, setLogoImage] = useState(() => localStorage.getItem(CACHE_KEY_LOGO));
  const [loginImage, setLoginImage] = useState(() => localStorage.getItem(CACHE_KEY_BG) || DEFAULT_BG);
  const [configLoaded, setConfigLoaded] = useState(() => !!localStorage.getItem(CACHE_KEY_BG));
  
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      if (user.rol === 'capacitador') {
        navigate('/capacitador/calendario');
      } else if (user.rol === 'trabajador' || user.tipo === 'trabajador') {
        navigate('/perfil-viewer');
      } else {
        navigate('/dashboard');
      }
    }
    fetchConfig();
  }, [user, navigate]);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/configuracion`);
      if (res.data.logo_image) {
        let imgUrl = res.data.logo_image;
        if (imgUrl.startsWith('/api/uploads/')) imgUrl = `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
        setLogoImage(imgUrl);
        localStorage.setItem(CACHE_KEY_LOGO, imgUrl);
      } else {
        setLogoImage(null);
        localStorage.removeItem(CACHE_KEY_LOGO);
      }
      if (res.data.login_image) {
        let imgUrl = res.data.login_image;
        if (imgUrl.startsWith('/api/uploads/')) imgUrl = `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
        setLoginImage(imgUrl);
        localStorage.setItem(CACHE_KEY_BG, imgUrl);
      } else {
        setLoginImage(DEFAULT_BG);
        localStorage.setItem(CACHE_KEY_BG, DEFAULT_BG);
      }
    } catch (e) {
      if (!loginImage) setLoginImage(DEFAULT_BG);
    }
    setConfigLoaded(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userData = await login(email, password);

      if (userData?.requiere_seleccion) {
        // Guardamos los perfiles en sessionStorage y vamos al selector
        sessionStorage.setItem('perfiles_disponibles', JSON.stringify(userData.perfiles));
        navigate('/seleccionar-perfil');
        return;
      }

      toast.success('¡Bienvenido!');
      
      // Redirigir según el tipo de usuario
      if (userData.rol === 'capacitador') {
        navigate('/capacitador/calendario');
      } else if (userData.rol === 'ese') {
        navigate('/ese/dashboard');
      } else if (userData.tipo === 'trabajador' || userData.rol === 'trabajador') {
        navigate('/perfil-viewer');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al iniciar sesión';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo Section - Centered */}
          <div className="flex justify-center mb-8" style={{ minHeight: '80px' }}>
            {configLoaded && (
              logoImage ? (
                <img
                  src={logoImage}
                  alt="Logo"
                  className="max-h-20 w-auto object-contain"
                  data-testid="login-logo"
                />
              ) : (
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center shadow-lg shadow-teal-200">
                    <Network className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Organigrama</h1>
                    <p className="text-sm text-slate-500">Sistema Organizacional</p>
                  </div>
                </div>
              )
            )}
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0 text-center">
              <CardTitle className="text-xl text-slate-900">Iniciar Sesión</CardTitle>
              <CardDescription>Ingresa tus credenciales para acceder al sistema</CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Correo electrónico</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                    className="focus-visible:ring-teal-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="password-input"
                      className="pr-10 focus-visible:ring-teal-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white font-medium shadow-sm transition-all active:scale-[0.98]"
                  disabled={loading}
                  data-testid="login-button"
                >
                  {loading ? 'Ingresando...' : 'Ingresar'}
                </Button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                  <div className="relative flex justify-center text-[10px] uppercase tracking-wider">
                    <span className="bg-white px-3 text-slate-400">¿Sin cuenta? · No account?</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate('/pase-visita')}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 transition-colors active:scale-[0.98]"
                  data-testid="pase-visita-btn"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Pase de Visita <span className="text-teal-500 font-normal mx-1">·</span> Visitor Pass
                </button>
                <p className="text-center text-[11px] text-slate-400 -mt-1">Inducción Visita + EPP · Autogestionado · 1 año</p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        {loginImage && (
          <>
            <img
              src={loginImage}
              alt="Corporate background"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-teal-500/80 to-teal-600/80" />
            <div className="absolute inset-0 flex items-center justify-center p-12">
              <div className="text-center text-white">
                <img
                  src="/assets/adn-glencore-logo.png"
                  alt="ADN Glencore"
                  className="w-40 h-40 object-contain mx-auto mb-6"
                  style={{ filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.18))' }}
                />
                <h2 className="text-4xl font-bold mb-4 tracking-tight">Gestión Organizacional</h2>
                <p className="text-lg text-teal-100 max-w-md">
                  Administra tu estructura organizacional, riesgos y capacitaciones de manera eficiente
                </p>
                <img
                  src="/assets/safework-logo.png"
                  alt="Safework"
                  className="h-12 object-contain mx-auto mt-5"
                  style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.2))' }}
                />
              </div>
            </div>
            {/* Footer centered on the image */}
            <div className="absolute bottom-4 left-0 right-0 text-center text-sm text-white/70">
              &copy; TystaCore {new Date().getFullYear()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
